"use client";

import { useState, useEffect, useRef } from "react";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";

// Only show the banner if connection has been down for this long.
// Silences brief drops: page load, schema pushes, websocket bounces.
const SHOW_DELAY_MS = 10_000;

export function OfflineBanner() {
    const { isOnline, wasOffline } = useNetworkStatus();

    const [visible, setVisible] = useState(false);
    const [mode, setMode] = useState<"offline" | "back">("offline");
    const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        clearTimeout(hideTimer.current);

        if (!isOnline) {
            setMode("offline");
            setVisible(true);
        } else if (wasOffline) {
            setMode("back");
            setVisible(true);
            hideTimer.current = setTimeout(() => setVisible(false), 3500);
        } else {
            setVisible(false);
        }

        return () => clearTimeout(hideTimer.current);
    }, [isOnline, wasOffline]);

    if (!visible) return null;

    const colors =
        mode === "back"
            ? { bg: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.35)", text: "#6ee7b7", dot: "#10b981" }
            : { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.3)", text: "#fca5a5", dot: "#ef4444" };

    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                gap: "7px",
                padding: "7px 12px 7px 9px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "'Inter', system-ui, sans-serif",
                letterSpacing: "0.01em",
                backdropFilter: "blur(14px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                animation: "olSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                color: colors.text,
                whiteSpace: "nowrap",
            }}
        >
            {/* Pulsing status dot */}
            <span
                style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: colors.dot,
                    flexShrink: 0,
                    animation: mode === "back" ? "none" : "olPulse 1.5s ease-in-out infinite",
                }}
            />

            {mode === "back" ? "Back online" : "No connection"}

            {mode === "offline" && (
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        marginLeft: "2px",
                        padding: "2px 9px",
                        borderRadius: "999px",
                        background: "rgba(255,255,255,0.12)",
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: "pointer",
                    }}
                >
                    Retry
                </button>
            )}

            <style>{`
        @keyframes olSlideIn {
          from { opacity: 0; transform: translateY(10px) scale(0.9); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes olPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
        </div>
    );
}
